import { useMemo } from 'react';
import { diffLines } from 'diff';

type DiffKind = 'add' | 'del' | 'ctx';

interface DiffRow {
  kind: DiffKind;
  text: string;
}

function computeDiffRows(before: string, after: string): DiffRow[] {
  const parts = diffLines(before, after);
  const rows: DiffRow[] = [];
  for (const part of parts) {
    const kind: DiffKind = part.added ? 'add' : part.removed ? 'del' : 'ctx';
    const lines = part.value.split('\n');
    // diffLines guarantees each chunk ends in \n, so the final split element is ''
    if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
    for (const text of lines) {
      rows.push({ kind, text });
    }
  }
  return rows;
}

interface DiffViewProps {
  before: string;
  after: string;
}

export function DiffView({ before, after }: DiffViewProps) {
  const rows = useMemo(() => computeDiffRows(before, after), [before, after]);
  const stats = useMemo(() => {
    let added = 0;
    let removed = 0;
    for (const r of rows) {
      if (r.kind === 'add') added += 1;
      else if (r.kind === 'del') removed += 1;
    }
    return { added, removed };
  }, [rows]);

  if (rows.length === 0) {
    return <p className="diff__empty">No textual difference.</p>;
  }

  return (
    <div className="diff" role="region" aria-label="Line-level diff">
      <div className="diff__stats" aria-hidden="true">
        <span className="diff__stat diff__stat--add">+{stats.added}</span>
        <span className="diff__stat diff__stat--del">−{stats.removed}</span>
      </div>
      <pre className="diff__body">
        {rows.map((row, i) => (
          <div key={i} className={`diff__line diff__line--${row.kind}`}>
            <span className="diff__marker" aria-hidden="true">
              {row.kind === 'add' ? '+' : row.kind === 'del' ? '−' : ' '}
            </span>
            <span className="diff__text">{row.text === '' ? ' ' : row.text}</span>
          </div>
        ))}
      </pre>
    </div>
  );
}
